import React, { useState, useEffect, useRef } from 'react';
// `CircularProgress` is deliberately NOT imported here — the indeterminate spinner it
// drove was removed at user request (see the history block at the scan chip below).
// The import and that JSX must always move together: a JSX-without-import mismatch is
// what produced `ReferenceError: CircularProgress is not defined` and blanked the whole
// app on every entry to this sheet, because there is no error boundary above
// CertificatePage. If you ever re-add the spinner, restore the import in the SAME edit.
import { Box, Stack, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { Button } from '@repo/ui';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { Tag, Check, RefreshCw, ArrowLeftIcon, ArrowDownIcon } from 'lucide-react';
import { createApiCall } from 'services';
import { isLocalhost } from 'utils';
import { PendingRing } from 'components/UI/PendingRing';
import { WhereToScanPanel } from './WhereToScanPanel';
import { sheetStepSx } from 'utils/pageTransition';

/**
 * WHAT THE SCAN IDENTIFIED, handed to `onScanSuccess`.
 *
 * Both fields are optional and both can be absent: the drawer's own scan path
 * completes a `?token=` round-trip through `/mobile/scan`, whose response
 * carries a redirect the certificate id can be read off — but the dev-only
 * simulate button below produces no tag at all, and neither does a caller who
 * only needs to know THAT a scan happened (the gift flow's case).
 *
 * A caller that needs an identifier must therefore handle its absence rather
 * than assume one. VRG-455's claim is exactly such a caller: with no identifier
 * there is no way to know which physical item is in the buyer's hand, which is
 * the whole reason the scan is load-bearing there.
 */
export interface ScanTagResult {
  /** `certificates.id`, read off `/mobile/scan`'s redirect when present. */
  certificateId?: string
  /** `tags.uid` — the chip UID, when the scan route yielded one directly. */
  tagUid?: string
}

interface ScanTagProps {
  /**
   * VRG-323 — where on THIS item the tag sits, in the brand's own words
   * ("Sewn into the inside left seam"). The column now exists, so this prop is the
   * feature rather than the seam it used to be.
   *
   * Arrives ALREADY RESOLVED from `certificate.product.scan_instructions`: the API
   * applies the product's value, else the company default, else null. The app does
   * NOT re-implement that fallback — the only tier it owns is null -> generic copy.
   *
   * Renamed from `tagLocation`. That name described the shape the prop was designed
   * for and never received — a bare noun phrase spliced into "near your {X} tag
   * location". What the column actually holds is free-form authored copy, and
   * interpolating a whole sentence into that template produces "…near your The tag
   * is sewn into the inside left seam. tag location." So the value is now used as
   * the sentence, and the name says what it is. No caller passed the old name (the
   * previous docstring records that none could), so nothing external changed.
   */
  scanInstructions?: string | null;
  /**
   * VRG-323 — absolute public URL of a still or clip showing where to scan, passed
   * straight through to `WhereToScanPanel`. Same already-resolved contract as
   * `scanInstructions`. `ScanTag` itself never renders it; it only owns the drawer
   * that does.
   */
  scanMediaUrl?: string | null;
  open: boolean;
  onClose: () => void;
  /**
   * VRG-455 — now receives WHAT was scanned, not just that a scan happened.
   *
   * Existing callers take no argument and are unaffected. The claim flow needs
   * the identifier because its next step is a server call that binds the
   * scanned item's certificate; without it, the page cannot know which unit the
   * buyer is holding.
   */
  onScanSuccess?: (result?: ScanTagResult) => void;
  /**
   * VERA-304 (A1) — HAND THE SCAN'S CREDENTIAL TO THE CALLER instead of spending
   * it here.
   *
   * By default this sheet POSTs the `?token=` it finds in the URL to
   * `/mobile/scan` itself and then calls `onScanSuccess`. That is right for a
   * caller whose flow only needs to know THAT a tag was read (the gift form
   * gates its submit on it). It is not enough for a caller whose SERVER needs to
   * be convinced: `onScanSuccess` is a boolean this component decided, and a
   * boolean proves nothing.
   *
   * When this prop is supplied it REPLACES the `/mobile/scan` POST — the token
   * goes to the caller, which spends it against its own endpoint and returns
   * whether that succeeded. The sheet still owns the visual state (`success` /
   * `error`), so a caller cannot show a green tick for a request the server
   * refused.
   *
   * Only one route consumes the token, deliberately: `/mobile/scan` and the
   * caller's endpoint both treat it as a single scan event, and the plain-text
   * decoder's constant counter means sending it to both would have them disagree
   * about `certificates.scan_counter`.
   */
  onScanToken?: (token: string) => Promise<boolean>;
  /**
   * VERA-304 (A1) — what the DEV simulate button does, when the caller needs it
   * to do more than flip this sheet to `success`.
   *
   * A caller using `onScanToken` has a server that will not accept a simulated
   * scan, so the default dev button — which just sets `success` — would show a
   * confirmation for something that did not happen. Supplying this lets the
   * caller obtain a REAL token by whatever dev-gated route it has and run the
   * identical code path, so what the preview demonstrates is the production
   * flow rather than a mock of it.
   */
  onSimulateScan?: () => Promise<boolean>;
  title?: string;
  pageTitle?: string;
  pageDescription?: string;
  /**
   * VERA-304 — terminal success rendered IN THIS DRAWER instead of opening another one.
   *
   * The gift flow used to stack three sheets: Gift form -> ScanTag -> GiftSendDrawer.
   * Two of them were titled "Gift", so the success screen appeared as a second identical
   * header sliding over the first. One task should be one surface: when a caller supplies
   * this, ScanTag keeps the drawer open on success, morphs the scan circle's glyph into
   * `successIcon`, and shows the caller's copy + action in place.
   *
   * Supplying `successTitle` also SUPPRESSES the 2s auto-close, because the drawer is now
   * the destination rather than a step being passed through.
   */
  successTitle?: string;
  successDescription?: string;
  /** Rendered under the copy — e.g. the gifted item's ProductCard. */
  successContent?: React.ReactNode;
  successActionLabel?: string;
  successActionOnClick?: () => void;
  /** Glyph the scan icon becomes on success. Defaults to a check. */
  successIcon?: React.ReactNode;
  /**
   * VRG-455 — the TONE of the caller-owned terminal screen.
   *
   * The `success*` slots are better read as "the caller owns what comes after
   * the scan", and what comes after is not always good news: a claim can scan
   * perfectly and then be refused for six different reasons, none of which is a
   * scan failure. Those need to land in THIS sheet rather than a second one
   * (§52: level two is a step machine, not a launchpad), and rendering them
   * behind a green check would be the sheet contradicting its own copy.
   *
   * `failure` tints the circle and the glyph with `error.main` at the same
   * weight the drawer's own "No tag detected" state uses, so the two failure
   * kinds read as the same thing. It does NOT change the copy, the actions or
   * the dismissal — those stay the caller's.
   */
  successTone?: 'success' | 'failure';
  /**
   * Extra controls under the caller's terminal action — a second, third and
   * fourth choice on a screen where a single "Done" is not enough.
   *
   * The failure branch needs this and the success branch never has: "we
   * couldn't find a purchase linked to your account" offers a recovery path AND
   * a way to reach the brand, and forcing that into one `successActionLabel`
   * would drop whichever one came second.
   */
  successExtraActions?: React.ReactNode;
  /**
   * VERA-304 — reveal the terminal screen as a SEQUENCE rather than all at once.
   *
   * Off, the success state swaps the scan circle's glyph for `successIcon` and shows
   * the caller's copy in one frame. That conflates two different facts into one mark:
   * the tag was READ, and the gift was SENT. The user only ever sees the second, so the
   * scan they were asked to perform is never acknowledged.
   *
   * On, the circle confirms the SCAN with a check, an arrow draws the eye downward, and
   * the caller's outcome (`successIcon` + title + description + content) arrives under
   * it. Same information, ordered as cause then effect.
   *
   * OPT-IN, deliberately, and this is the load-bearing part of the prop's existence:
   * `CertificatePage`'s "Possession verified" screen passes `successIcon` expecting it
   * IN THE CIRCLE, so flipping the layout for every caller would silently restyle a
   * surface this change was never asked to touch. A caller opts in when the sequence
   * actually says something — for the gift flow, "scanned, therefore sent".
   *
   * Layout is committed on the first success frame and only opacity/transform animate
   * (see `revealSx`), so the staging cannot reflow the sheet mid-animation — the
   * "really jumpy and odd" resize working-conventions §52 forbids.
   */
  successStagedReveal?: boolean;
}

/**
 * `verifying` (VERA-304) is the window between "a token exists" and "the server has
 * answered" — the ONLY state in this component where something is genuinely in
 * flight. `active` is the opposite: armed and waiting on a human to move a phone,
 * which is why the indeterminate spinner was removed from it seven times over (see
 * the history at the scan chip). Separating the two is what lets the ring say
 * "working" without reintroducing that.
 */
type ScanStatus = 'idle' | 'active' | 'verifying' | 'success' | 'error';

/**
 * The scan chip's diameter.
 *
 * A named constant because TWO things must agree on it: the chip's own box, and
 * `PendingRing`, which rides its BORDER and therefore has to be the same outer
 * diameter, not something smaller. Two hand-typed 88s is exactly how a ring ends up
 * concentric-but-inset, which is the defect the chip's own history records at step 6.
 */
const SCAN_CHIP = 88;

/**
 * FIXED height of the sheet body while stepping (working-conventions §52).
 *
 * 382 = the taller of the two steps, measured at 375x812 with the worst case actually
 * on screen: the product-override tier WITH media (a 240px-capped image plus a
 * two-line instruction), not the text-only fallback. The scan step measures 380, so
 * this costs it 2px and buys an exact match.
 *
 * A `height`, not the `minHeight` this started as. A floor only stops the sheet
 * SHRINKING, and the guidance step is the taller one — so with a floor a brand with
 * media grew the sheet on step change, which is the same "jumpy and odd" resize §52
 * forbids, just in the other direction.
 *
 * A brand who writes instructions long enough to exceed this scrolls INSIDE the step
 * (see the panel's `overflowY`) rather than resizing the sheet. Scrolling a little is
 * a much smaller cost than a sheet that jumps every time you ask where the tag is.
 */
const STEP_HEIGHT = 382;

/**
 * Pull the scanned certificate's id out of `/mobile/scan`'s redirect.
 *
 * Returns `undefined` rather than throwing on anything unexpected — a missing
 * or malformed redirect must not fail a scan that otherwise succeeded, because
 * most callers never look at the result at all. `window.location.origin` is
 * supplied as a base so a RELATIVE redirect parses; `new URL('/x')` with no
 * base throws, and that throw would land inside the scan's own try/catch and
 * paint an error state on a successful scan.
 */
const parseScanRedirect = (redirectUrl?: unknown): ScanTagResult | undefined => {
  if (typeof redirectUrl !== 'string' || !redirectUrl) return undefined;
  try {
    const url = new URL(redirectUrl, window.location.origin);
    const certificateId = url.searchParams.get('certificate_id') ?? undefined;
    return certificateId ? { certificateId } : undefined;
  } catch {
    return undefined;
  }
};

/**
 * VERA-304: the one source for the colour of BOTH scan animations (pulse rings and
 * progress arc). They are a single visual — a ring turning inside a ring expanding —
 * so reading the palette twice is how they drift apart on the next edit.
 *
 * In `apps/app` these two branches are the same colour by construction, not a
 * fallback chain that happens to work: `settings/theme.ts` sets `palette.brand` to
 * `brandRamp(primary)`, and `brandRamp`'s step 600 is the source colour unmixed. The
 * `|| primary.main` arm is for the case where a theme layer forgets to define the
 * ramp — see `brandRamp.ts`, which exists because `brand.*` used to fall through to
 * `@repo/ui`'s Vera purple on every tenant. Falling back to `primary` keeps that from
 * being reintroduced here.
 */
const brandColor = (theme: Theme) =>
  (theme.palette as any)?.brand?.[600] || theme.palette.primary.main;

/**
 * VERA-304 — the staged success reveal (`successStagedReveal`).
 *
 * Duration and curve are `utils/pageTransition`'s page push, NOT a new easing: this
 * app already has exactly one "content arrives" motion and a second one invented here
 * would read as a different product one sheet over. Imported as literals rather than
 * from that module because `PAGE_SLIDE_MS` is typed for a page-level slide and this is
 * a 8px content rise — same feel, different magnitude.
 *
 * ONLY `opacity` and `transform` animate. Nothing here changes layout, so every staged
 * element occupies its final position from the first success frame and the sheet cannot
 * grow or shrink while the sequence plays (working-conventions §52). That is also why
 * the stages are CSS `animation-delay` rather than `setTimeout` + state: a timer would
 * mount each piece in turn, and mounting is a reflow.
 *
 * `both` fill-mode matches the two enter animations already in this feature
 * (`veraScanSuccessPop` directly below, and `RecipientSearchPicker`'s `rowEnterSx`), so
 * the sheet has one convention rather than three.
 */
const REVEAL_MS = 260;
const REVEAL_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Stage offsets, in ms after the success frame.
 *
 * The check's own pop is 420ms, so the arrow starts just before it settles (360) and
 * the outcome lands after (520) — overlapping rather than queued, which reads as one
 * gesture continuing instead of three separate things appearing. Total ~780ms, which is
 * a confirmation the user is meant to read, not a step they are waiting through.
 */
const ARROW_DELAY_MS = 360;
const OUTCOME_DELAY_MS = 520;

const revealSx = (delayMs: number) => ({
  animation: `veraScanRevealIn ${REVEAL_MS}ms ${REVEAL_EASE} ${delayMs}ms both`,
  '@keyframes veraScanRevealIn': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  // Motion is decoration — the confirmation is equally readable without it, and a
  // staged reveal is exactly the kind of sequenced movement this setting suppresses.
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

export const ScanTag = ({
  open,
  onClose,
  onScanSuccess,
  onScanToken,
  onSimulateScan,
  successTitle,
  successDescription,
  successContent,
  successActionLabel,
  successActionOnClick,
  successIcon,
  successTone = 'success',
  successExtraActions,
  successStagedReveal = false,
  title = 'Scan tag',
  pageTitle = 'Scan the tag',
  // VERA-304 copy fix: was "Hold your phone near the item to scan the Tag" — it
  // restated the heading ("Scan the tag") almost verbatim and capitalised "Tag"
  // mid-sentence, which appears nowhere else in the app. The body should add the
  // one thing the heading doesn't say: WHERE on the phone. Matches the wording in
  // WhereToScanPanel so the two surfaces agree.
  pageDescription,
  scanInstructions,
  scanMediaUrl,
}: ScanTagProps) => {
  // VERA-304: names WHERE the tag is, because "against the tag" assumes the user
  // already knows — which is exactly what someone reaching for help does not.
  //
  // VRG-323: the brand's own instructions are used AS this sentence, not spliced
  // into a template (see `scanInstructions` for why the template had to go).
  //
  // `pageDescription` still wins when a caller sets it, because that copy describes
  // the TASK rather than the tag — GiftOutActionsSection's "…confirm gift
  // cancelation" would be replaced by scan instructions that say nothing about
  // cancelling. Nothing is lost by that ordering: the brand's instructions and media
  // are always one tap away in the "Where to scan?" drawer below.
  //
  // `||` on the instructions, not `??`: a column that has been set and then cleared
  // arrives as `''`, which is non-nullish and would otherwise render an empty
  // sentence. Whitespace-only copy collapses the same way.
  const description =
    pageDescription ??
    (scanInstructions?.trim() ||
      'Hold the top of your phone near the tag location on your item.');
  // VERA-304: there used to be a client-side-only countdown (`useState(300)` +
  // `setTimeout`) that flipped `status` to 'error' at zero. It had NO server-side
  // backing — no token TTL, no reservation expiry — so its only real effect was to
  // manufacture a failure out of the user simply taking their time to scan. It was
  // the sole generator of the (also-wrong) "Something went wrong" error state.
  // `status` can still become 'error' from the real failure path below (the
  // `/mobile/scan` POST catch block) — just never from a fake clock.
  const [status, setStatus] = useState<ScanStatus>('idle');
  /**
   * WHICH STEP THIS SHEET IS SHOWING — working-conventions §52.
   *
   * "Where to scan" used to be a THIRD bottom sheet opened on top of this one, which
   * put the gift form behind two scrims and left its `Done` on screen beside this
   * sheet's `Cancel` with nothing to say which unwound what. It is now a step of THIS
   * sheet, swapped in place.
   *
   * That is the same move this component already makes for its terminal success state
   * (see the `successTitle` prop) — one task, one surface. This is that fix applied to
   * the help side of the scan rather than the success side.
   */
  const [step, setStep] = useState<'scan' | 'where'>('scan');

  /*
   * Direction for the in-sheet push: forward (into help) enters from the RIGHT, back
   * from the LEFT — the same convention page navigation uses, so "deeper" moves the
   * same way whether it is a page or a sheet. `null` on a re-render where the step did
   * not change, so the animation is not replayed on every state tick (`status` changes
   * under it while the NFC read is pending).
   */
  const prevStep = useRef(step);
  const enterFrom = prevStep.current === step ? null : step === 'where' ? 'right' : 'left';
  useEffect(() => {
    prevStep.current = step;
  }, [step]);

  useEffect(() => {
    if (open) {
      setStatus('active');
    } else {
      setStatus('idle');
    }
    // Always reopen on the SCAN step. A sheet that reopens where it was last left
    // shows help to someone who asked to scan, and the back arrow would be the only
    // way forward from a screen they never chose.
    setStep('scan');
    prevStep.current = 'scan';
  }, [open]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!open || !token) return;

    const finalize = async () => {
      try {
        // VERA-304: from here until the server answers, a request IS in flight, so
        // the chip carries the ring loader. Set before either branch — both of them
        // await a network round-trip.
        setStatus('verifying');
        // VERA-304: a caller that supplied `onScanToken` spends the token
        // itself — see that prop. The token is handed over INSTEAD of being
        // POSTed here, never as well as, so exactly one route consumes it.
        if (onScanToken) {
          const ok = await onScanToken(token);
          setStatus(ok ? 'success' : 'error');
          if (ok) onScanSuccess?.();
          return;
        }
        const resp = await createApiCall({ method: 'POST', url: '/mobile/scan', data: { token } });
        if (resp?.status === 200) {
          setStatus('success');
          onScanSuccess?.(parseScanRedirect(resp?.data?.redirectUrl));
        } else {
          setStatus('error');
        }
      } catch (e) {
        setStatus('error');
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url.toString());
      }
    };

    finalize();
  }, [open]);

  useEffect(() => {
    // VERA-304: a caller that supplies its own success screen OWNS the dismissal — the
    // drawer is the destination now, not a step. Auto-closing after 2s would yank a
    // terminal screen (and its "View certificate" action) out from under the user.
    // A scan can complete while the user is reading the help step (the tag is read by
    // the OS, not by anything on screen). Snap back to the scan step so the success
    // state is what they see, rather than help with a silently-succeeded scan behind it.
    if (status === 'success') setStep('scan');
    if (status === 'success' && !successTitle) {
      const t = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [status, onClose, successTitle]);

  return (
      <DrawerPopup
        // VERA-304: the header has to move with the state. Caught in the walkthrough —
        // after a successful gift the drawer still read "Confirm gift" / "Cancel",
        // i.e. it asked the user to confirm something already done and offered to
        // cancel something no longer cancellable from there. On a caller-owned
        // terminal screen the title becomes that screen's own title and the action
        // becomes "Done", which dismisses rather than implying an undo.
        // §52: ONE header and ONE dismissal for the whole sheet, whichever step is
        // showing. The help step retitles the header and adds a BACK arrow beside the
        // title; it deliberately does NOT add a second right-hand action, because the
        // `Done`-next-to-`Cancel` ambiguity is exactly what stacking a third drawer
        // produced. `Cancel` stays as the sheet's single dismissal throughout.
        title={
          step === 'where'
            ? 'Where to scan'
            : status === 'success' && successTitle
              ? successTitle
              : title
        }
        headerLeft={
          step === 'where' ? (
            <Box
              sx={{
                /*
                 * LAYOUT height 24, TAP height 40, and the split is the point.
                 *
                 * The title's line box is 24px. A 40px control in the same row made the
                 * header 73px against the scan step's 57px, and that 16px moved the whole
                 * sheet on step change — measured 501 -> 517, the exact resize §52 forbids.
                 *
                 * Negative margins were tried first and do not work here: `height` from
                 * `sx` lands on this element but `ml`/`my` compute to 0px (verified in the
                 * browser, on a cold server). Rather than keep fighting the cascade, the
                 * tap target is an absolutely-positioned overlay below — out of flow, so
                 * it cannot contribute height no matter what wins the cascade, and the
                 * 40px target survives.
                 */
                position: 'relative',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.primary',
                flexShrink: 0,
              }}
            >
              <ArrowLeftIcon size={20} />
              <Box
                onClick={() => setStep('scan')}
                role='button'
                tabIndex={0}
                aria-label='Back to scanning'
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setStep('scan');
                  }
                }}
                // inset -8 grows the 24px box to a 40x40 target without taking part in
                // layout. Transparent: the affordance is the arrow, not a chip.
                sx={{ position: 'absolute', inset: '-8px', cursor: 'pointer' }}
              />
            </Box>
          ) : undefined
        }
        rightActionLabel={status === 'success' && successTitle ? 'Done' : 'Cancel'}
        rightActionOnClick={onClose}
        child={
        <Box
          key={step}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            // VERA-304: dropped `minHeight: '500px'`. It forced a tall sheet in EVERY
            // state, so the short error/success states floated their content in the
            // middle of a mostly-empty drawer. DrawerPopup's bottom sheet is already
            // content-height (`height: auto`, `maxHeight: 90vh`) — VERA-842 made that
            // the default precisely so a sheet matches its content — and this
            // minHeight was opting out of it.
            height: '100%',
            /*
             * §52: "do not resize the sheet between steps." The two steps have
             * genuinely different natural heights (a scan circle plus two buttons vs a
             * 240px-capped image plus two lines), and a sheet that grows or shrinks as
             * you step through it is the "really jumpy and odd" behaviour the picker's
             * fixed height exists to prevent.
             *
             * FIXED for the two stepped states, so scan <-> where cannot move the sheet
             * at all. This began as a `minHeight` floor and that was not enough: a floor
             * only stops the sheet shrinking, and the guidance step is the TALLER one,
             * so a brand with media grew it instead. Measured 501px -> 517px before the
             * fix, with the worst case on screen rather than the empty one.
             *
             * The SUCCESS state is the one exception and keeps a floor: it renders a
             * caller-supplied ProductCard whose height this component cannot know, so
             * pinning it would clip someone else's content — and success is terminal
             * rather than a step the user moves back and forth between, so growing into
             * it is not the jump §52 is about.
             */
            ...(status === 'success' && successTitle
              ? { minHeight: STEP_HEIGHT }
              : { height: STEP_HEIGHT }),
            ...sheetStepSx(enterFrom),
          }}
        >
          {step === 'where' ? (
            /* The help step. The three fallback tiers (product -> company default ->
               the generic VERA explainer) all live inside this panel; ScanTag only
               forwards the already-resolved values. */
            <WhereToScanPanel instructions={scanInstructions} mediaUrl={scanMediaUrl} />
          ) : (
          <>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              py: 4,
            }}
          >
            <Box
              sx={theme => ({
                width: SCAN_CHIP,
                height: SCAN_CHIP,
                borderRadius: '50%',
                // VERA-304: the error fill was the SOLID `error[200]` (#FECDCA), a
                // saturated pink that read as a filled alert badge rather than a
                // status ring. Matched to how the platform's encode/create-certificate
                // modals tint a failure — `alpha(error.main, 0.12)` (see
                // EncodeSessionBody) — so the red BORDER carries the signal and the
                // fill just supports it, instead of the fill shouting over it.
                backgroundColor:
                  // VRG-455: a caller-owned TERMINAL screen can be bad news (a
                  // claim that scanned fine and was then refused), so it takes
                  // the same tint the drawer's own failure state takes rather
                  // than sitting on neutral grey under red copy.
                  status === 'error' ||
                  (status === 'success' && successTitle && successTone === 'failure')
                    ? alpha(theme.palette.error.main, 0.12)
                    : theme.palette.grey[50],
                // VERA-304: the ring is now CONSTANT — always 1px, only its colour
                // changes. Previously the border appeared only on error (`'none'`
                // otherwise), so the circle's box model changed between states and the
                // glyph inside shifted by a pixel as it did. Keeping the geometry fixed
                // means idle -> active -> error/success swaps only fill + icon, which is
                // what should be carrying the state change.
                // `!important` for the same reason the pulse rings 60 lines below carry it,
                // and this is the SIXTH time this trap has bitten. `index.css` has
                // `#vera-shadow-root * { @apply border-border }` — an ID selector (0-1-0-0)
                // setting border-colour on EVERY element, which outranks the single emotion
                // class `sx` compiles to (0-0-1-0). So the colour above was authored,
                // computed, and discarded: both branches rendered `rgb(228,228,231)`.
                //
                // Which means THE ERROR STATE'S RED RING HAS NEVER RENDERED. It hid because
                // the idle branch resolves to `grey[200]` (#E4E4E7), close enough to the
                // token that nothing looked wrong — the bug was only visible in the state
                // nobody screenshots. The comment directly above explains at length why the
                // border is constant so that only its COLOUR carries the state change, and
                // the colour was the part that did not work.
                border: `1px solid ${
                  status === 'error' ||
                  (status === 'success' && successTitle && successTone === 'failure')
                    ? theme.palette.error.main
                    : theme.palette.grey[200]
                } !important`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 4,
                // anchor for the progress ring below
                position: 'relative',
              })}
            >
              {/* VERA-304: the scanner app's PULSE, in the BRAND colour.
                  A ring scaling out while fading, 3s ease-out, mirroring
                  apps/vera-app's `NfcTagPulse` idle rings so the two surfaces read
                  as the same product. Two rings offset by half the cycle so the ripple
                  is continuous rather than strobing.

                  The ring was `grey[400]`; it is now brand, per user request ("when
                  scanned the pulse should pulse the primary color"). Note `brand[600]`
                  is not a Vera token in this app — `settings/mui/brandRamp.ts` derives
                  the whole ramp from the tenant's Studio `primary_color`, and step 600
                  IS that colour unmixed (MIX[600] = 0). So this pulses the brand the
                  user is actually looking at, and degrades to neutral (the default
                  tenant primary is black) rather than to Vera purple.

                  Start opacity dropped 0.6 -> 0.45 to hold the ring at its previous
                  visual weight. Composited over the `grey[50]` (#f5f5f5) fill, the old
                  grey[400] (#888888) at 0.6 gives 1.91:1; brand at 0.45 — taking Vera
                  purple #7F48DF as the worked example — gives 1.93:1. Left at 0.6 the
                  purple would have been ~50% more contrasty than the grey it replaced
                  (4.95:1 vs 3.25:1 at full opacity), which is a louder ring than anyone
                  asked for; the colour is the change, not the weight. */}
              {status === 'active' &&
                [0, 1.5].map(delay => (
                  <Box
                    key={delay}
                    /*
                     * VRG-549: the handle the Studio preview's token highlight points at
                     * when the operator asks what `primary_color` edits. These rings are
                     * the survey's headline example of a surface nobody would guess a
                     * "button colour" reaches, and they are bare `Box`es with no
                     * addressable class of their own — emotion's generated names are not
                     * reachable from outside. See `pages/StudioPreviewPage/tokenHighlight.ts`.
                     */
                    data-vera-scan-pulse=""
                    sx={theme => ({
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      // `!important` is LOAD-BEARING, not defensive. index.css sets
                      // `#vera-shadow-root * { border-color: … }`, and an ID selector
                      // (0-1-0-0) outranks the single emotion class emotion writes for
                      // sx (0-0-1-0) — so without this the ring's colour is authored,
                      // computed, and then discarded. Measured before the fix: the ring
                      // rendered `rgb(228,228,231)` (#E4E4E7, the `border.subtle` token)
                      // instead of brand, i.e. the brand pulse was silently still grey.
                      // Same trap documented in settings/tokens.ts and MuiAlert.ts; it
                      // has now caused this class of bug four times in this app.
                      border: `1px solid ${brandColor(theme)} !important`,
                      animation: 'veraScanPulse 3s ease-out infinite',
                      animationDelay: `${delay}s`,
                      '@keyframes veraScanPulse': {
                        '0%': { transform: 'scale(1)', opacity: 0.45 },
                        '100%': { transform: 'scale(1.6)', opacity: 0 },
                      },
                      // Only the FIRST ring survives with motion off. Both are pinned at
                      // scale(1) once the animation stops, i.e. exactly on the circle's
                      // own border — two of them there double-paint to ~0.7 and read as
                      // a heavy second border. One static brand ring still says "armed".
                      '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                        opacity: delay === 0 ? 0.45 : 0,
                      },
                    })}
                  />
                ))}
              {/* VERA-304 — the indeterminate spinner is REMOVED. The chip's own static
                  ring (the 1px border on the Box above) stays and is a different element;
                  conflating the two is what produced the wrong instruction that preceded
                  this edit. The full history is kept because this element has now flipped
                  seven times and every flip was a user call:

                    1. Originally a 40px arc that REPLACED the icon while active.
                    2. Removed, on the argument that an arc promises progress an NFC wait
                       does not have — nothing advances until the user moves the phone.
                    3. Re-added at explicit user request ("while the scan has loading
                       spinner around it"), this time LAYERED at 60px so the icon stayed put.
                    4. Removed again (5f6b2ab0b), because the pulse had become BRAND-coloured
                       and the circle then carried three stacked moving or coloured elements.
                    5. Re-added by ACCIDENT — the one to learn from. VERA-315 (d47f2ad8a)
                       branched BEFORE step 4 and resized the arc 60 -> 88; the later merge
                       (085661d74) took step 4's DELETED IMPORT together with VERA-315's
                       SURVIVING JSX. That compiles to a free identifier, so React threw
                       `CircularProgress is not defined` on the first render where status
                       became 'active' — white-screening the whole app for anyone opening any
                       scan drawer (gift, gift-in, gift-out, transfer-in). Invisible to review
                       because the JSX sat directly under a comment saying it had been
                       removed, and invisible to CI because apps/app carries a ~168-error tsc
                       baseline that the resulting TS2304 simply joined.
                    6. Re-added DELIBERATELY at 88px, user request repeated twice: "the
                       loader around scan tag should be border of the gray chip - not
                       inset". At 60 it read as a second, smaller circle concentric with the
                       chip — two rings, not one busy chip.
                    7. REMOVED (CURRENT), user: "dont remove the arc - remove the
                       indeterminate circular loader." The chip carries TWO ring-shaped
                       things and step 6 had merged them in everyone's head: the static 1px
                       border on the Box above (the "arc" the user means, KEPT) and this
                       spinning indeterminate CircularProgress (REMOVED). Once the spinner
                       traced that same border at the same radius they were visually one
                       object, which is exactly why the ask reads as contradictory unless
                       you separate them. Removing the spinner also restores 2 and 4: an
                       indeterminate spinner promises progress an NFC wait does not have —
                       nothing advances until the user moves the phone — and the brand pulse
                       already says "armed" without it.

                  ⚠️ THE IMPORT AND THIS BLOCK MUST MOVE TOGETHER, and they now agree that
                  there is NO spinner: the JSX is gone and `CircularProgress` is gone from
                  the import at the top of this file. Step 5 is what happens when they come
                  apart, and a merge is where it happens — main deleted both while a branch
                  kept both, so either side alone was consistent and the combination was not.
                  If you re-add the element, restore the import in the SAME edit, and update
                  this comment — a comment that disagrees with the code is what hid that bug
                  for a week. Do not "clean up" a stray `CircularProgress` import here
                  either; an unused import is how the next merge resurrects the JSX. */}
              {/* VERA-304 — THE RING, while the server is being asked.
                  This is NOT a reinstatement of the removed spinner (steps 1–7
                  above), and the distinction is the whole justification: every one
                  of those was on `active`, the ARMED state, where nothing advances
                  until the user moves their phone, so an indeterminate arc promised
                  progress that did not exist. `verifying` is the opposite — a token
                  has been handed to the server and a request will resolve on its
                  own. That is exactly what an indeterminate ring means.

                  It is the SHARED `PendingRing` (§36), the same one the
                  claim-ownership confirmation uses on its own 88px badge, so the
                  two flows cannot drift into two different loaders. Step 6's
                  requirement — "the loader should be the border of the gray chip,
                  not inset" — is satisfied by construction: the component sizes
                  itself to the badge's outer diameter and rides the border.

                  The `Tag` glyph below STAYS put underneath it. The chip is a
                  target holding a phone against a tag; swapping its mark mid-check
                  would restage the screen for a sub-second state. */}
              {status === 'verifying' && <PendingRing size={SCAN_CHIP} />}
              {status !== 'success' && (
                <Box
                  sx={theme => ({
                    display: 'flex',
                    color: status === 'error' ? theme.palette.error.main : theme.palette.grey[500],
                  })}
                >
                  {/* VERA-304: `Tag`, per user request, replacing `ScanLine`.
                      (Chevron -> ScanLine -> Tag; each step was a user call.)

                      The choice was Tag vs the company logo, and Tag wins for two
                      reasons. The circle is a TARGET the user holds a phone against,
                      so it should depict the thing being scanned, not who made it;
                      and a brand logo here would be the loudest element in a circle
                      that already carries two brand-coloured animations, inverting
                      the hierarchy the pulse exists to set.

                      Held at 32px / strokeWidth 1 / grey[500] so it stays subordinate
                      to the pulse and the success glyph that replaces it. */}
                  <Tag size={32} strokeWidth={1} />
                </Box>
              )}
              {status === 'success' && (
                <Box
                  sx={theme => ({
                    display: 'flex',
                    color:
                      successTitle && successTone === 'failure'
                        ? theme.palette.error.main
                        : brandColor(theme),
                    // VERA-304: the glyph ARRIVES rather than swapping. The scan circle
                    // spends the whole wait as an expanding pulse around a static mark;
                    // cutting straight to a different mark reads as a re-render, not as
                    // "it happened". A short scale-up with a slight overshoot (1.15 at
                    // 60%) borrows the pulse's own outward motion and lands on it, so the
                    // circle finishes the gesture it was making.
                    animation: 'veraScanSuccessPop 420ms cubic-bezier(.34,1.56,.64,1) both',
                    '@keyframes veraScanSuccessPop': {
                      '0%': { transform: 'scale(.6)', opacity: 0 },
                      '60%': { transform: 'scale(1.15)', opacity: 1 },
                      '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                    // Motion-sensitive users get the end state with no travel.
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  })}
                >
                  {/* VERA-304: in the STAGED sequence this circle answers one question
                      only — was the tag read? — so it is always the check, and the
                      caller's own glyph moves down to the outcome block where it
                      belongs. Unstaged callers keep the original behaviour exactly:
                      their glyph replaces the check in place. */}
                  {successStagedReveal ? <Check size={40} /> : successIcon ?? <Check size={40} />}
                </Box>
              )}
            </Box>

            {/* VERA-304: the arrow that makes the two marks one statement — scanned,
                THEREFORE sent. Without it the check and the outcome are just two things
                stacked, and the user has to infer the causal link that the whole screen
                exists to assert. `aria-hidden`: it is a visual connector between two
                elements that both already carry their own text, so announcing it would
                add a turn to the screen-reader pass and say nothing. */}
            {status === 'success' && successStagedReveal && (
              <Box
                aria-hidden
                sx={theme => ({
                  display: 'flex',
                  justifyContent: 'center',
                  color: theme.palette.text.secondary,
                  mb: 2,
                  ...revealSx(ARROW_DELAY_MS),
                })}
              >
                <ArrowDownIcon size={20} strokeWidth={1.5} />
              </Box>
            )}

            <Stack spacing={2}>
              {status !== 'success' && status !== 'error' && (
                <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>{pageTitle}</Typography>
              )}
              {status === 'error' && (
                <Typography
                  sx={theme => ({
                    fontSize: '16px',
                    fontWeight: 500,
                    color: theme.palette.error.main,
                  })}
                >
                  No tag detected
                </Typography>
              )}
              {/* VERA-304: the caller's terminal copy when it owns this success screen,
                  otherwise the generic "Tag scanned" for callers still using ScanTag as
                  a pass-through step.

                  Grouped into ONE nested Stack so the staged reveal has a single element
                  to animate. The inner `spacing={2}` reproduces the outer Stack's own
                  gap, so an unstaged caller renders pixel-identically to before — the
                  wrapper is structural, not visual. */}
              {status === 'success' && (
                <Stack
                  spacing={2}
                  sx={successStagedReveal ? revealSx(OUTCOME_DELAY_MS) : undefined}
                >
                  {/* The caller's glyph, displaced from the circle above (which now
                      carries the scan's check). Only in staged mode — otherwise it is
                      still up in the circle and rendering it here would double it. */}
                  {successStagedReveal && successIcon && (
                    <Box
                      sx={theme => ({
                        display: 'flex',
                        justifyContent: 'center',
                        color: brandColor(theme),
                      })}
                    >
                      {successIcon}
                    </Box>
                  )}
                  <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
                    {successTitle ?? 'Tag scanned'}
                  </Typography>
                  {successDescription && (
                    <Typography
                      sx={theme => ({
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: 1.5,
                        color: theme.palette.text.secondary,
                      })}
                    >
                      {successDescription}
                    </Typography>
                  )}
                  {successContent && (
                    <Box sx={{ mt: 1, textAlign: 'left' }}>{successContent}</Box>
                  )}
                </Stack>
              )}
              {status !== 'error' && status !== 'success' && (
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: theme => theme.palette.text.secondary,
                  }}
                >
                  {/* VERA-304: the standing instruction is to hold the phone near
                      the tag — which stops being true the moment the tag has been
                      read and the server is answering. Leaving it up asks for an
                      action that is already done. One line, same slot, so the block
                      does not change height under the ring. */}
                  {status === 'verifying' ? 'Checking the tag…' : description}
                </Typography>
              )}
              {status === 'error' && (
                <Typography
                  sx={theme => ({
                    fontSize: '14px',
                    fontWeight: 400,
                    color: theme.palette.text.primary,
                  })}
                >
                  Hold your phone near the tag and try again.
                </Typography>
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              textAlign: 'center',
              // VERA-304: NO local `pb`. DrawerPopup's shared `py: 3` (24px) already
              // provides the bottom inset for every drawer, and the last control here
              // is a `text` Button carrying its own 16px paddingBottom. A filled
              // button's background reaches its box edge, so box == visual — but a text
              // link has no background, so that padding becomes invisible whitespace
              // that STACKS on the container's. With the old `pb: 2` the measured gap
              // was box 40px / visual 56px, i.e. 16px looser than every filled-button
              // drawer. Dropping it lands the visual gap back on 40px (24 shared + 16
              // from the link's own padding), matching the Gift drawer exactly.
              display: 'flex',
              flexDirection: 'column',
              // §29: button groups are 12px. apps/app is on MUI's 8px base (NOT
              // @repo/ui's 4px), so 12px is `1.5` here — copying `3` from a platform
              // file would render 24px.
              gap: 1.5,
            }}
          >
            {/* VRG-323: this opens the brand's OWN "where to scan" content — their
                instructions and their still or clip — with the generic VERA
                explainer now only the last-resort tier. It is a plain toggle
                because both the copy and the media resolution live in
                WhereToScanPanel; ScanTag just forwards what the certificate
                carried. */}
            {/* VERA-304: `secondary`, from @repo/ui — not a bare text link.
                An earlier pass made this `text` so it wouldn't compete with the
                error state's primary "Try again"; that reasoning was wrong on the
                IDLE state, which is where this control mostly lives and where it is
                the ONLY thing to press. A shared secondary reads as an action while
                still deferring to a primary when one appears. */}
            {/* VERA-304: hidden once this drawer is showing a caller-owned TERMINAL
                screen. "Where to scan?" answers a question the user no longer has — the
                scan already happened — and leaving it there gives the success state two
                competing controls. */}
            {!(status === 'success' && successTitle) && (
              <Button
                variant='secondary'
                sx={{ width: '100%', paddingX: 2 }}
                onClick={() => setStep('where')}
              >
                Where to scan?
              </Button>
            )}

            {/* The caller's terminal action (e.g. "View certificate"). Primary, because
                on this screen it is the only thing to do. */}
            {status === 'success' && successTitle && successActionLabel && (
              <Button
                variant='primary'
                sx={{ width: '100%', paddingX: 2 }}
                onClick={successActionOnClick}
              >
                {successActionLabel}
              </Button>
            )}
            {/* VRG-455: the caller's SECOND and third choices. A refused claim
                offers both a recovery path and a way to reach the brand, and a
                single primary action slot would silently drop one of them. */}
            {status === 'success' && successTitle && successExtraActions}

            {/* VERA-304: was wrapped in a `pt: 2` Box, which STACKED on the parent's
                `gap` — 16px + 16px = 32px between the two buttons, roughly double the
                12px button-group standard (§29). The wrapper is gone; the parent's gap
                is now the single source of that spacing. */}
            {/* VERA-304 — DEV ONLY: simulate the tap.
                This scan is NOT reachable by the Labs scan-simulator. Labs advances a
                certificate that is already in a scan-gated TRANSFER status; this one
                fires before any transfer row exists (it gates gift SUBMIT), and its
                only real input is a `?token=` from a physical NFC redirect into
                `/mobile/scan`. Without this control the whole gift flow is unwalkable
                on a desktop preview — which is exactly where it gets verified.

                Gated on BOTH `VITE_DEV_SESSION` and a loopback hostname — the same
                two conditions as the dev customer-session auto-login in
                `store/auth/saga.tsx`, and for the same reason. The flag alone is not
                enough. No deploy or CI config sets `VITE_DEV_SESSION`, so a correct
                production build statically replaces it with `undefined` and drops
                this block as dead code — but that protects a correctly configured
                build, not a mis-configured one. The flag also turns on auto-login, so
                anyone who sets it on a deployed preview to skip the login round-trip
                would, without the hostname check, silently ship a control that
                completes an ownership transfer with no physical tag. `isLocalhost()`
                is a runtime check that holds even when the flag is wrong.

                It calls the identical `onScanSuccess` the real scan path calls, so it
                exercises the same downstream code rather than a parallel branch.

                VERA-304 (A1): a caller may override what this button DOES, via
                `onSimulateScan`. It has to be overridable because the default —
                flip to `success` locally — is a lie for any caller whose server
                must be convinced a scan happened; that caller instead mints a real
                token behind its own dev gate and runs the production path. The
                button's GATING is unchanged and stays here either way. */}
            {/* Also hidden while `verifying` — the button is async and a second press
                would fire a second request against a token the first one is already
                spending. */}
            {import.meta.env.VITE_DEV_SESSION &&
              isLocalhost() &&
              status !== 'success' &&
              status !== 'verifying' && (
              <Button
                variant='text'
                sx={{ width: '100%', paddingX: 2, opacity: 0.7 }}
                onClick={async () => {
                  if (onSimulateScan) {
                    // VERA-304: the override mints a real token and runs the
                    // production request, so it goes through `verifying` exactly as
                    // the NFC path does — otherwise the ring would be unreachable on
                    // the only surface it can be walked on (a desktop preview has no
                    // radio). The no-override branch below is a local flip with no
                    // request, so it must NOT claim to be verifying anything.
                    setStatus('verifying');
                    const ok = await onSimulateScan();
                    setStatus(ok ? 'success' : 'error');
                    if (ok) onScanSuccess?.();
                    return;
                  }
                  setStatus('success');
                  onScanSuccess?.();
                }}
              >
                Simulate scan (dev)
              </Button>
            )}

            {status === 'error' && (
              <Button
                sx={{ width: '100%', paddingX: 2 }}
                variant='primary'
                startIcon={<RefreshCw size={16} />}
                onClick={() => {
                  setStatus('active');
                }}
              >
                Try again
              </Button>
            )}
          </Box>
          </>
          )}
        </Box>
      }
      handleOpen={open}
      handleClose={onClose}
    />
  );
};
